// `<details>` toggles.
//
// Why a node and not raw HTML: `<details><summary>t</summary>\n\nbody\n\n</details>`
// with blank lines is split by marked's HTML block rule (it ends at a blank
// line) into "opening tag + top-level blocks + closing tag". Block ids and
// conversations would then attach to the body's paragraphs, and all of them
// would go stale once a tokenizer was added later.
//
// Based on `@tiptap/extension-details` (MIT). Upstream maps it to Pandoc-style
// `:::details`; trameria's canonical markdown is raw HTML that GitHub folds and
// agents can read, so the markdown tokenizer, parser and renderer are replaced.
//
// ## Canonical form (the only form that becomes a node)
//
//     <details><summary>Title</summary>
//     (blank line)
//     body (the same blocks as the top level, lexed recursively)
//     (blank line)
//     </details>
//
// `<details open>` is allowed (mapped to the `open` attribute).
//
// ## Anything else returns undefined
//
// No summary, no closing tag, one line, no blank lines, other attributes...: the
// tokenizer returns undefined and marked's `html` block makes it an opaque block.
// Bytes are kept: synthesizing `<summary></summary>` for a missing summary would
// change the text, so opening an existing document would record a revision. The
// canonical form applies only to toggles made in the editor.
//
// ## It runs before the opaque block
//
// `MarkdownManager` registers tokenizers with `marked.use({ extensions })`, and
// marked's `blockTokens` always tries the extension block tokenizers before the
// built-in ones (`html` among them). The opaque block has no tokenizer of its
// own (it rides on the built-in `html` token), so this one always runs first,
// whatever the registration order.

import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
} from "@tiptap/core";
import {
  Details as BaseDetails,
  DetailsContent as BaseDetailsContent,
  DetailsSummary as BaseDetailsSummary,
} from "@tiptap/extension-details";
import { Lines } from "./lines";
import { renderBlockSiblings } from "./render-blocks";

/** The opening line: `<details>` / `<details open>` + `<summary>...</summary>` ending the line. */
const OPEN_LINE_RE = /^<details( open)?><summary>(.*)<\/summary>$/;

const CLOSE_LINE = "</details>";

/**
 * CommonMark fences: a run of 3+ backticks or tildes opens one, and only a line
 * of the same character at least as long closes it. Lines inside are never
 * read again (a `</details>` inside a fence does not close the toggle).
 */
const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})(.*)$/;

interface FenceState {
  markerChar: string;
  length: number;
}

function fenceOpen(line: string): FenceState | undefined {
  const match = FENCE_OPEN_RE.exec(line);
  if (!match) return undefined;
  // A backtick fence whose info string contains a backtick does not open (CommonMark).
  if (match[1][0] === "`" && match[2].includes("`")) return undefined;
  return { markerChar: match[1][0], length: match[1].length };
}

function fenceCloses(line: string, open: FenceState): boolean {
  const trimmed = line.trim();
  if (trimmed.length < open.length) return false;
  return [...trimmed].every((ch) => ch === open.markerChar);
}

/** Opening `<details` tags minus `</details>` on a line. */
function countTags(line: string): number {
  const closes = line.match(/<\/details>/g)?.length ?? 0;
  const opens = line.match(/<details(?=[\s>])/g)?.length ?? 0;
  return opens - closes;
}

interface DetailsScan {
  open: boolean;
  summary: string;
  /** The body source (with a trailing newline). */
  content: string;
  /** Characters consumed. */
  rawLength: number;
}

/**
 * Whether `src` starts with a canonical toggle, by scanning lines; undefined
 * otherwise (the caller falls back to an opaque block). The end is found by
 * counting `<details` / `</details>` outside fences, so neither a closing tag in
 * a fence nor a nested toggle ends it early.
 */
export function scanCanonicalDetails(src: string): DetailsScan | undefined {
  const lines = new Lines(src);

  const openMatch = OPEN_LINE_RE.exec(lines.at(0) ?? "");
  if (!openMatch) return undefined;
  // A summary containing `</summary>` (a greedy match) is not canonical.
  if (openMatch[2].includes("</summary>") || openMatch[2].includes("<summary>"))
    return undefined;
  if (lines.at(1) !== "") return undefined;

  let depth = 1;
  let fence: FenceState | undefined;

  for (
    let i = 2, line = lines.at(i);
    line !== undefined;
    line = lines.at(++i)
  ) {
    if (fence) {
      if (fenceCloses(line, fence)) fence = undefined;
      continue;
    }
    const opened = fenceOpen(line);
    if (opened) {
      fence = opened;
      continue;
    }

    depth += countTags(line);
    if (depth > 0) continue;

    // The line that brings the depth to 0 must be `</details>` alone, after a
    // blank line; otherwise give up.
    if (line !== CLOSE_LINE) return undefined;
    if (lines.at(i - 1) !== "") return undefined;
    // After the closing line: a blank line or the end. With text right after,
    // the toggle and that text would become two blocks joined by a blank line,
    // changing the bytes of a document that used to be one HTML block.
    const after = lines.at(i + 1);
    if (after !== undefined && after !== "") return undefined;
    const content = lines.join(2, i - 1);
    // `detailsContent` is `block+`: an empty body cannot be represented.
    if (content.trim() === "") return undefined;

    const consumed = lines.join(0, i + 1);
    return {
      open: openMatch[1] !== undefined,
      summary: openMatch[2],
      content: `${content}\n`,
      // Consume the line break after the closing line (if any); later blank
      // lines are marked's `space` token, like after a built-in fence.
      rawLength: consumed.length + (src.length > consumed.length ? 1 : 0),
    };
  }

  // No closing tag before the end.
  return undefined;
}

/**
 * The toggle: a container (not an atom) whose body is the same blocks as the
 * top level. `persist: true` makes upstream keep `open` as an attribute: it is
 * written as `<details open>`, so it is part of the content.
 */
export const Details = BaseDetails.extend({
  markdownTokenName: "details",

  markdownTokenizer: {
    name: "details",
    level: "block" as const,
    start(src: string) {
      const match = src.match(/^<details(?: open)?><summary>/m);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string, _tokens: MarkdownToken[], lexer) {
      const scan = scanCanonicalDetails(src);
      if (!scan) return undefined;

      // marked lexes list item content with `state.top = false`, and
      // `blockTokens` entered from there returns paragraphs as block-level
      // `text` tokens, which would put raw text directly under `detailsContent`
      // (`block+`). As marked does for loose lists, block-level `text` is read
      // as `paragraph` (its inline tokens are kept).
      const tokens = lexer
        .blockTokens(scan.content)
        .map((t) =>
          t.type === "text"
            ? ({ ...t, type: "paragraph" } as MarkdownToken)
            : t,
        );

      // Nothing lexed (the body is only an indented code block, say): cannot
      // be `block+`, so fall back to an opaque block.
      if (tokens.length === 0) return undefined;

      return {
        type: "details",
        raw: src.slice(0, scan.rawLength),
        open: scan.open,
        summary: scan.summary,
        tokens,
      };
    },
  },

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    // `parseBlockChildren` (not `parseChildren`) gives the same result as the
    // top level, implicit empty paragraphs from blank lines included.
    const parseBlockChildren = h.parseBlockChildren ?? h.parseChildren;
    const summary = token.summary as string;
    const content = parseBlockChildren(token.tokens ?? []);

    // Tokens `@tiptap/markdown` cannot handle (an indented code block's `code`
    // token is dropped even at the top level) can leave no children. Give up on
    // the toggle and keep the consumed source as an opaque block, so the bytes
    // survive.
    if (content.length === 0) {
      const raw = ((token.raw as string | undefined) ?? "").replace(/\s+$/, "");
      return h.createNode("opaqueBlock", { raw }, []);
    }

    return h.createNode("details", { open: token.open === true }, [
      h.createNode(
        "detailsSummary",
        undefined,
        summary.length > 0 ? [h.createTextNode(summary)] : [],
      ),
      h.createNode("detailsContent", undefined, content),
    ]);
  },

  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
    const children = node.content ?? [];
    const summaryNode = children[0];
    const contentNode = children[1];
    const renderChild = h.renderChild;
    const render = (child: JSONContent | undefined, index: number) =>
      child === undefined
        ? ""
        : (renderChild?.(child, index) ?? h.renderChildren([child]));

    const openAttr = node.attrs?.open ? " open" : "";
    const summary = render(summaryNode, 0);
    // The toggle decides the blank lines, so the body's own leading / trailing
    // line breaks (a table's) are dropped; otherwise two blank lines would
    // follow `</summary>` and reparsing would add an empty paragraph.
    const content = render(contentNode, 1).replace(/^\n+|\n+$/g, "");
    return `<details${openAttr}><summary>${summary}</summary>\n\n${content}\n\n${CLOSE_LINE}`;
  },
}).configure({ persist: true });

/**
 * A tokenizer that never matches, replacing upstream's Pandoc-style
 * `:::<node name>` tokenizers. With them, a line like `:::detailsSummary` in a
 * document (in a text about Pandoc, say) made a `detailsSummary` directly under
 * the doc - an invalid document whose text was lost on save. `.extend({
 * markdownTokenizer: undefined })` cannot remove it (undefined falls back to the
 * parent), so it is overridden with this.
 */
export function neverMatchingTokenizer(name: string) {
  return {
    name,
    level: "block" as const,
    start: () => -1,
    tokenize: () => undefined,
  };
}

/**
 * The `<summary>` text. Upstream's content is `text*`; it passes through as
 * plain text (reading `**bold**` as strong would change the bytes).
 */
export const DetailsSummary = BaseDetailsSummary.extend({
  markdownTokenName: "detailsSummary",
  markdownTokenizer: neverMatchingTokenizer("detailsSummary"),

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const text = (token.text as string | undefined) ?? "";
    return h.createNode(
      "detailsSummary",
      undefined,
      text.length > 0 ? [h.createTextNode(text)] : [],
    );
  },

  renderMarkdown(node: JSONContent) {
    return (node.content ?? []).map((child) => child.text ?? "").join("");
  },
});

/** The toggle body (`block+`), joined like the doc. */
export const DetailsContent = BaseDetailsContent.extend({
  markdownTokenName: "detailsContent",
  markdownTokenizer: neverMatchingTokenizer("detailsContent"),

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const parseBlockChildren = h.parseBlockChildren ?? h.parseChildren;
    return h.createNode(
      "detailsContent",
      undefined,
      parseBlockChildren(token.tokens ?? []),
    );
  },

  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
    return renderBlockSiblings(node.content ?? [], h);
  },
});
